import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { startWith, debounceTime } from 'rxjs/operators';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { SetupFormResultI, SearchSymbolResults, SymbolSearchService, AlgoStrategy, QuoteStoreService, Quote, defaultAlgoParams, MarketSocketService } from '@shared/providers';
import { map, first } from 'lodash';
import { NgxToastrService } from '../../../../providers';
import { REG_EX, MAX_SYMBOLS_IN_STRATEGY } from '@app/_helpers';
import { MarketIDMap, FEE_SCHEDULES_STRINGS } from '@app/_shared/shared.enums';
import { ActivatedRoute } from '@angular/router';
import { symbolAditionLimit, SYMBOL_ADDITION_IN_UPDATE } from '@app/_shared/strings';

interface SelectedSymbol extends SearchSymbolResults {
    quoteObj?: Quote
}

@Component({
    selector: 'app-strategy-setup',
    templateUrl: './strategy-setup.component.html',
    styleUrls: ['./strategy-setup.component.scss']
})
export class StrategySetupComponent implements OnInit, OnDestroy {
    symbols: SelectedSymbol[] = [];
    searchedSymbols = [];
    symbolSearchControl = new UntypedFormControl();
    isValid = false;
    ALGO_NAME_REGEX = REG_EX.id;

    private subscriptions = new Subscription();
    differentSegmentError: boolean;
    MAX_SYMBOLS_IN_STRATEGY = MAX_SYMBOLS_IN_STRATEGY;

    update = false;

    // setupFormGroup = new FormGroup({
    //     algoName: new FormControl([], [Validators.required]), // Add Alphanumeric check
    //     algoNotes: new FormControl([], [])
    // });

    constructor(
        private symbolSearchService: SymbolSearchService,
        private snackBar: NgxToastrService,
        private quoteStoreService: QuoteStoreService,
        private router: ActivatedRoute,
        private marketSocketService: MarketSocketService
    ) { }

    ngOnInit(): void {
        this.subscriptions.add(
            this.symbolSearchControl.valueChanges
                .pipe(
                    startWith(''),
                    debounceTime(300)
                ).subscribe(input => {
                    this.searchedSymbols = [];
                    if (!input || !input.length) { return; }
                    this.searchedSymbols = this.symbolSearchService.search(input).filter(s => !this.symbols.includes(s));
                })
        )

        this.symbolSearchService.onLoad.subscribe((loaded) => {
            if (loaded) {
                this.router.queryParamMap.subscribe(data => {
                    if (data.keys.length) {
                        const mwData = JSON.parse(data.get('marketData'));
                        const symbol = this.symbolSearchService.findScripsBySecurity([{
                            security_id: mwData.security_id,
                            market_id: mwData.market_id
                        }])[0];
                        this.subscribeSymbol(symbol);
                        this.symbols.push(symbol);
                        this.updateIsValid();
                    }
                })
            }
        })
    }

    removeSymbol(symbol: SelectedSymbol) {
        this.unSubscribeSymbol(symbol);
        this.differentSegmentError = false;
        this.symbols.splice(this.symbols.indexOf(symbol), 1);
        this.updateIsValid();
    }

    addSymbol(event: MatAutocompleteSelectedEvent) {
        if (this.symbols.length < (this.update ? 1 : MAX_SYMBOLS_IN_STRATEGY)) {
            const selectedSymbol: SearchSymbolResults = event.option.value;
            this.differentSegmentError = false;
            if (this.symbols.length) {
                // Only add symbols from one kind of segment only.
                if (selectedSymbol.market_id.slice(-2) !== first(this.symbols).market_id.slice(-2)) {
                    this.symbolSearchControl.reset();
                    this.differentSegmentError = true;
                    return;
                }
            }
            this.subscribeSymbol(selectedSymbol);
            this.symbols.push(event.option.value);
            this.updateIsValid();
            this.symbolSearchControl.reset();
        } else {
            this.snackBar.simpleCustomToaster(this.update ? SYMBOL_ADDITION_IN_UPDATE : symbolAditionLimit(MAX_SYMBOLS_IN_STRATEGY));
            this.symbolSearchControl.reset();

        }
    }

    subscribeSymbol(symbol: SelectedSymbol) {
        symbol.quoteObj = this.quoteStoreService.getFeedObj(MarketIDMap[symbol.market_id], symbol.security_id);
        this.marketSocketService.subscribeSymbol({ market_id: MarketIDMap[symbol.market_id], security_id: symbol.security_id });
    }

    unSubscribeSymbol(symbol: SearchSymbolResults) {
        this.marketSocketService.unSubscribeSymbol({ market_id: MarketIDMap[symbol.market_id], security_id: symbol.security_id });
    }

    fillUpdateForm(update: AlgoStrategy) {
        // this.setupFormGroup.patchValue(update);
        this.symbolSearchService.onLoad.subscribe(loaded => {
            if (loaded) {
                if (!this.router.snapshot.queryParamMap.has('marketData')) {
                    this.update = !!update.symbols.length;
                    this.symbols = this.symbolSearchService.findScripsBySecurity(update.symbols);
                    // Removing undefined/removed symbols from the symbols list
                    this.symbols = this.symbols.filter(symbol => symbol !== undefined);
                    this.symbols.forEach((symbol) => {
                        this.subscribeSymbol(symbol);
                    })
                };
                this.updateIsValid();
            }
        })
        this.updateIsValid();
    }

    getData(): SetupFormResultI {
        this.updateIsValid();
        const data: SetupFormResultI = {
            symbols: map(this.symbols, (symbol) => {
                return {
                    security_id: symbol.security_id,
                    market_id: symbol.market_id
                }
            }),
            algoName: '',
            feeSchedule: FEE_SCHEDULES_STRINGS.MONTHLY,
            maximumMultiplier: defaultAlgoParams.maximumMultiplier,
            minimumCapital: defaultAlgoParams.minimumCapital,
            minimumMultiplier: defaultAlgoParams.minimumMultiplier,
            monthlyFee: defaultAlgoParams.monthlyFee,
            public: defaultAlgoParams.public,
            tags: defaultAlgoParams.tags,
            variableFee: defaultAlgoParams.variableFee
        };
        return data;
    }

    updateIsValid() {
        // this.isValid = !!(this.setupFormGroup.valid && this.symbols.length);
        this.isValid = !!this.symbols.length;
    }

    ngOnDestroy() {
        this.symbols.forEach(s => {
            this.unSubscribeSymbol(s);
        })
    }
}
